import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, formatMoney } from '../constants';

export default function usePayments({ getAuthHeader, getAmount, getMode, getName, getContributionStatus, isPaid: isPaid, generateRecuReference, getAllPayments: getAllPayments, fetchData }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showSEPAModal, setShowSEPAModal] = useState(false);
  const [showEditAcompteModal, setShowEditAcompteModal] = useState(false);
  const [showDeleteAcompteModal, setShowDeleteAcompteModal] = useState(false);
  // V37.38 — Modal de rectification de la date de paiement sur recu de contribution
  const [showEditPaymentDateModal, setShowEditPaymentDateModal] = useState(false);
  const [editPaymentDateForm, setEditPaymentDateForm] = useState({ date_paiement: '', motif: '' });
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedAcompte, setSelectedAcompte] = useState(null);
  const [selectedAcompteIndex, setSelectedAcompteIndex] = useState(null);
  const [selectedEcheanceIndex, setSelectedEcheanceIndex] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [stripePaymentType, setStripePaymentType] = useState('card');

  const [paymentForm, setPaymentForm] = useState({
    reference: '', date: new Date().toISOString().split('T')[0],
    mode: 'virement', amount: '', notes: '', echeanceIndex: null
  });

  const [sepaForm, setSepaForm] = useState({
    customerName: '', customerEmail: '', iban: '', sepaType: 'core'
  });

  const [editAcompteForm, setEditAcompteForm] = useState({
    montant: '', date: '', mode: 'virement', reference: '', observations: '', statut: 'recu'
  });

  // Open payment modal
  const openPaymentModal = (doc, echeanceIndex = null) => {
    setSelectedDoc(doc);
    const echeances = doc?.echeancier_personnalise?.echeances || [];
    const echeance = echeanceIndex !== null ? echeances[echeanceIndex] : null;
    setPaymentForm({
      reference: echeance ? generateRecuReference(doc, echeanceIndex) : (doc?.reference || ''),
      date: new Date().toISOString().split('T')[0],
      mode: getMode(doc),
      amount: echeance ? echeance.montant : '',
      notes: echeance ? `Paiement echeance ${echeanceIndex + 1}/${echeances.length}` : '',
      echeanceIndex,
      paymentType: echeance ? 'acompte' : 'total',
      isScheduled: false
    });
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!selectedDoc) return;
    if (paymentForm.paymentType === 'acompte' && !paymentForm.amount) {
      toast.error("Veuillez saisir le montant de l'acompte"); return;
    }
    setProcessing(true);
    try {
      await axios.post(`${API}/domium/subscribe/admin/${selectedDoc.reference}/confirm-payment`, {
        reference_paiement: paymentForm.reference, date_paiement: paymentForm.date,
        mode_paiement: paymentForm.mode,
        montant_partiel: paymentForm.paymentType === 'acompte' ? parseFloat(paymentForm.amount) : null,
        observations: paymentForm.notes, echeance_index: paymentForm.echeanceIndex,
        is_acompte: paymentForm.paymentType === 'acompte', is_scheduled: paymentForm.isScheduled
      }, { headers: getAuthHeader() });
      const typeLabel = paymentForm.paymentType === 'acompte' ? 'Acompte' : 'Paiement';
      if (paymentForm.echeanceIndex !== null) toast.success(`Echeance ${paymentForm.echeanceIndex + 1} validee`);
      else if (paymentForm.paymentType === 'acompte') toast.success(`${typeLabel} de ${formatMoney(paymentForm.amount)} enregistre`);
      else toast.success(`${typeLabel} valide`);
      setShowPaymentModal(false);
      fetchData();
    } catch { toast.error("Erreur lors de l'enregistrement du paiement"); }
    finally { setProcessing(false); }
  };

  // Acompte CRUD
  const openEditAcompteModal = (doc, acompte, index) => {
    setSelectedDoc(doc); setSelectedAcompte(acompte); setSelectedAcompteIndex(index);
    setEditAcompteForm({
      montant: acompte.montant || '', date: acompte.date || '',
      mode: acompte.mode || 'virement', reference: acompte.reference || '',
      observations: acompte.observations || '',
      statut: acompte.statut || (acompte.is_scheduled ? 'programme' : 'recu')
    });
    setShowEditAcompteModal(true);
  };

  const openDeleteAcompteModal = (doc, acompte, index) => {
    setSelectedDoc(doc); setSelectedAcompte(acompte); setSelectedAcompteIndex(index);
    setShowDeleteAcompteModal(true);
  };

  const handleEditAcompte = async () => {
    if (!selectedDoc || selectedAcompteIndex === null) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/domium/subscribe/admin/${selectedDoc.reference}/acompte/${selectedAcompteIndex}`, {
        montant: parseFloat(editAcompteForm.montant), date: editAcompteForm.date,
        mode: editAcompteForm.mode, reference: editAcompteForm.reference,
        observations: editAcompteForm.observations, statut: editAcompteForm.statut
      }, { headers: getAuthHeader() });
      toast.success('Acompte modifie avec succes');
      setShowEditAcompteModal(false); fetchData();
    } catch { toast.error('Erreur lors de la modification'); }
    finally { setProcessing(false); }
  };

  const handleDeleteAcompte = async () => {
    if (!selectedDoc || selectedAcompteIndex === null) return;
    setProcessing(true);
    try {
      await axios.delete(`${API}/domium/subscribe/admin/${selectedDoc.reference}/acompte/${selectedAcompteIndex}`, { headers: getAuthHeader() });
      toast.success('Acompte annule avec succes');
      setShowDeleteAcompteModal(false); fetchData();
    } catch { toast.error("Erreur lors de l'annulation"); }
    finally { setProcessing(false); }
  };

  // Mark acquitted
  const handleMarkAcquitted = async (doc) => {
    if (!doc) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/domium/subscribe/admin/${doc.reference}/mark-acquitted`, {}, { headers: getAuthHeader() });
      toast.success('Recu de contribution marque comme ACQUITTE');
      fetchData();
    } catch { toast.error('Erreur lors du marquage'); }
    finally { setProcessing(false); }
  };

  // V37.38 — Rectification de la date de paiement sur recu de contribution (DOS DOMIUM)
  // Permet au Super Admin de corriger la date d'un paiement deja enregistre, pour
  // mise a jour comptable. Le PDF du recu est regenere a la volee au prochain telechargement.
  const openEditPaymentDateModal = (doc) => {
    if (!doc) return;
    setSelectedDoc(doc);
    const currentDate = doc?.paiement?.date_paiement || '';
    // Normaliser au format YYYY-MM-DD pour <input type="date">
    const isoDate = currentDate ? String(currentDate).split('T')[0].slice(0, 10) : new Date().toISOString().split('T')[0];
    setEditPaymentDateForm({ date_paiement: isoDate, motif: '' });
    setShowEditPaymentDateModal(true);
  };

  const handleUpdatePaymentDate = async () => {
    if (!selectedDoc || !editPaymentDateForm.date_paiement) {
      toast.error('Veuillez saisir une date de paiement valide');
      return;
    }
    setProcessing(true);
    try {
      const response = await axios.put(
        `${API}/domium/subscribe/admin/${selectedDoc.reference}/recu-payment-date`,
        {
          date_paiement: editPaymentDateForm.date_paiement,
          motif: editPaymentDateForm.motif || null,
        },
        { headers: getAuthHeader() },
      );
      toast.success(response.data?.message || 'Date de paiement mise a jour');
      setShowEditPaymentDateModal(false);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur lors de la mise a jour de la date de paiement';
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  // Stripe
  const openStripeModal = (doc, echeanceIndex = null) => {
    setSelectedDoc(doc); setSelectedEcheanceIndex(echeanceIndex);
    setStripePaymentType('card'); setShowStripeModal(true);
  };

  const handleStripePayment = async () => {
    if (!selectedDoc) return;
    setProcessing(true);
    try {
      const amount = selectedEcheanceIndex !== null
        ? selectedDoc.echeancier_personnalise?.echeances?.[selectedEcheanceIndex]?.montant
        : getAmount(selectedDoc);
      const response = await axios.post(`${API}/domium/stripe/create-checkout`, {
        reference: selectedDoc.reference, amount, echeance_index: selectedEcheanceIndex,
        payment_method: stripePaymentType, origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      if (response.data.success && response.data.checkout_url) {
        toast.success('Redirection vers le paiement securise...');
        window.open(response.data.checkout_url, '_blank');
        setShowStripeModal(false);
      } else toast.error('Erreur lors de la creation du paiement');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur paiement Stripe'); }
    finally { setProcessing(false); }
  };

  // SEPA
  const openSEPAModal = (doc) => {
    setSelectedDoc(doc);
    const isEntreprise = doc.identite?.siret || doc.identite?.raison_sociale;
    setSepaForm({
      customerName: getName(doc), customerEmail: doc.identite?.email || '',
      iban: '', sepaType: isEntreprise ? 'b2b' : 'core'
    });
    setShowSEPAModal(true);
  };

  const handleSetupSEPA = async () => {
    if (!selectedDoc) return;
    setProcessing(true);
    try {
      const echeances = selectedDoc.echeancier_personnalise?.echeances || [];
      if (echeances.length === 0) { toast.error("Veuillez d'abord creer un echeancier"); setProcessing(false); return; }
      const response = await axios.post(`${API}/domium/stripe/setup-sepa-schedule`, {
        reference: selectedDoc.reference, echeances, customer_email: sepaForm.customerEmail,
        customer_name: sepaForm.customerName, iban: sepaForm.iban,
        sepa_type: sepaForm.sepaType, origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      if (response.data.success && response.data.setup_url) {
        toast.success(`Configuration SEPA ${sepaForm.sepaType === 'b2b' ? 'B2B' : 'Core'} lancee...`);
        window.open(response.data.setup_url, '_blank');
        setShowSEPAModal(false); fetchData();
      } else toast.error('Erreur lors de la configuration SEPA');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur configuration SEPA'); }
    finally { setProcessing(false); }
  };

  const handleExecuteSEPAPayment = async (doc, echeanceIndex) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/domium/stripe/execute-sepa-payment/${doc.reference}?echeance_index=${echeanceIndex}`, {}, { headers: getAuthHeader() });
      if (response.data.success) { toast.success(`Prelevement SEPA execute - ${formatMoney(response.data.amount)}`); fetchData(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur prelevement SEPA'); }
    finally { setProcessing(false); }
  };

  // Check Stripe return params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    const sepaSetup = urlParams.get('sepa_setup');
    if (paymentStatus === 'success' && sessionId) {
      (async () => {
        try {
          const response = await axios.get(`${API}/domium/stripe/checkout/status/${sessionId}`, { headers: getAuthHeader() });
          if (response.data.payment_status === 'paid') { toast.success('Paiement Stripe confirme !'); fetchData(); }
        } catch {}
      })();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Paiement annule'); window.history.replaceState({}, '', window.location.pathname);
    } else if (sepaSetup === 'success') {
      toast.success('Configuration SEPA reussie !'); fetchData();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [getAuthHeader, fetchData]);

  // Download docs
  const downloadDoc = async (doc, type) => {
    try {
      let url = '', name = '';
      if (type === 'attestation') {
        const isSigned = doc?.signature?.statut === 'signed' || getContributionStatus(doc) === 'acquitted';
        url = isSigned ? `${API}/domium/documents/${doc.reference}/attestation` : `${API}/domium/documents/${doc.reference}/attestation-draft`;
        name = isSigned ? `Attestation_Signee_${doc.reference}.pdf` : `Attestation_Draft_${doc.reference}.pdf`;
      } else if (type === 'recu') {
        url = `${API}/domium/subscribe/${doc.reference}/download/recu-ess`;
        name = `Recu_ESS_${doc.reference}.pdf`;
      }
      if (!url) return;
      const res = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = name; link.click();
      toast.success('Telechargement reussi');
    } catch { toast.error('Document non disponible'); }
  };

  const archiveDoc = async (doc) => {
    try {
      await axios.post(`${API}/domium/subscribe/admin/${doc.reference}/archive-recus`, {}, { headers: getAuthHeader() });
      toast.success('Archive dans la GED');
    } catch { toast.error('Erreur archivage'); }
  };

  return {
    showPaymentModal, setShowPaymentModal, showStripeModal, setShowStripeModal,
    showSEPAModal, setShowSEPAModal, showEditAcompteModal, setShowEditAcompteModal,
    showDeleteAcompteModal, setShowDeleteAcompteModal,
    // V37.38 — Modify payment date on contribution receipt
    showEditPaymentDateModal, setShowEditPaymentDateModal,
    editPaymentDateForm, setEditPaymentDateForm,
    openEditPaymentDateModal, handleUpdatePaymentDate,
    selectedDoc, setSelectedDoc, selectedAcompte, selectedAcompteIndex,
    selectedEcheanceIndex, processing, setProcessing,
    stripePaymentType, setStripePaymentType,
    paymentForm, setPaymentForm, sepaForm, setSepaForm,
    editAcompteForm, setEditAcompteForm,
    openPaymentModal, handlePayment,
    openEditAcompteModal, openDeleteAcompteModal, handleEditAcompte, handleDeleteAcompte,
    handleMarkAcquitted,
    openStripeModal, handleStripePayment,
    openSEPAModal, handleSetupSEPA, handleExecuteSEPAPayment,
    downloadDoc, archiveDoc,
  };
}
